import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Inbox, Lock, LogOut, RefreshCw, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_EMAIL = "admin@futurefactory.group";
const EASE = [0.33, 1, 0.68, 1];

const formatDate = (ts) => {
  try {
    return new Date(ts).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase();
  } catch {
    return ts;
  }
};

const Bureau = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem("ffg_bureau_token"));
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [authenticating, setAuthenticating] = useState(false);
  const [enquiries, setEnquiries] = useState(null);

  const load = useCallback(
    async (t) => {
      try {
        const { data } = await axios.get(`${API}/enquiries`, {
          headers: { Authorization: `Bearer ${t}` },
        });
        setEnquiries(data);
      } catch (e) {
        if (e.response?.status === 401) {
          sessionStorage.removeItem("ffg_bureau_token");
          setToken(null);
          setEnquiries(null);
          toast.error("Session expired. Re-enter your access key.");
        } else {
          toast.error("Failed to load enquiries.");
        }
      }
    },
    []
  );

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    if (authenticating) return;
    setAuthenticating(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email: ADMIN_EMAIL, password });
      sessionStorage.setItem("ffg_bureau_token", data.token);
      setToken(data.token);
      setPassword("");
      toast.success("Access granted. Channel secure.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Access denied.");
    } finally {
      setAuthenticating(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/enquiries/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEnquiries((list) => list.filter((x) => x.id !== id));
      toast.success("Enquiry purged from the log.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const logout = () => {
    sessionStorage.removeItem("ffg_bureau_token");
    setToken(null);
    setEnquiries(null);
  };

  if (!token) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-abyss px-6 py-36" data-testid="bureau-login-page">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="hud-corners w-full max-w-md border border-white/15 bg-surface p-8 md:p-12"
        >
          <div className="flex h-12 w-12 items-center justify-center border border-neon/50 bg-neon/5">
            <Lock className="h-5 w-5 text-neon" strokeWidth={1.5} />
          </div>
          <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.35em] text-neon">
            // Restricted Channel
          </p>
          <h1 className="mt-4 font-display text-2xl font-medium tracking-tight md:text-3xl" data-testid="bureau-login-title">
            Contact Bureau
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-secondary">
            Enter your access key to review incoming project enquiries.
          </p>
          <form onSubmit={login} className="mt-8 space-y-5" data-testid="bureau-login-form">
            <div>
              <label htmlFor="bureau-key" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-white/45">
                Access Key
              </label>
              <input
                id="bureau-key"
                data-testid="bureau-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••••"
                className="w-full border border-white/15 bg-white/[0.03] px-4 py-3.5 text-sm text-white placeholder:text-white/25 outline-none transition-colors duration-300 focus:border-cyber"
              />
            </div>
            {error && (
              <p className="border border-red-500/40 bg-red-500/10 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-red-400" data-testid="bureau-login-error">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="bureau-login-button"
              disabled={authenticating}
              className="w-full border border-neon bg-neon/10 py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-neon transition-all duration-300 hover:bg-neon hover:text-[#0B0C10] disabled:opacity-50"
            >
              {authenticating ? "Verifying..." : "Unlock Bureau"}
            </button>
          </form>
        </motion.div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-abyss pb-32 pt-36 md:pt-44" data-testid="bureau-inbox-page">
      <div className="mx-auto max-w-[1200px] px-6 md:px-12">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-neon md:text-sm">
              // Contact Bureau
            </p>
            <h1 className="mt-4 font-display text-3xl font-medium tracking-tight sm:text-4xl md:text-5xl" data-testid="bureau-inbox-title">
              Enquiries Inbox
            </h1>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-white/35" data-testid="bureau-inbox-count">
              {enquiries ? `${enquiries.length} transmission${enquiries.length === 1 ? "" : "s"} logged` : "Scanning channel..."}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              data-testid="bureau-refresh-button"
              onClick={() => load(token)}
              className="flex items-center gap-2 border border-white/15 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 transition-colors duration-300 hover:border-cyber hover:text-cyber"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </button>
            <button
              data-testid="bureau-logout-button"
              onClick={logout}
              className="flex items-center gap-2 border border-white/15 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 transition-colors duration-300 hover:border-red-400 hover:text-red-400"
            >
              <LogOut className="h-3.5 w-3.5" />
              Lock
            </button>
          </div>
        </div>

        <div className="mt-14 space-y-4" data-testid="bureau-enquiry-list">
          {enquiries && enquiries.length === 0 && (
            <div className="flex flex-col items-center border border-dashed border-white/15 py-24" data-testid="bureau-empty-state">
              <Inbox className="h-8 w-8 text-white/20" strokeWidth={1.25} />
              <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.3em] text-white/35">
                Channel clear. No transmissions yet.
              </p>
            </div>
          )}
          {enquiries &&
            enquiries.map((enquiry, i) => (
              <motion.article
                key={enquiry.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: i * 0.06, ease: EASE }}
                className="group border border-white/10 bg-surface p-6 transition-colors duration-300 hover:border-cyber/50 md:p-8"
                data-testid={`enquiry-card-${i}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="border border-cyber/40 bg-cyber/5 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.3em] text-cyber">
                      {enquiry.project_type}
                    </span>
                    <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/35">
                      {formatDate(enquiry.timestamp)}
                    </span>
                  </div>
                  <button
                    data-testid={`enquiry-delete-${i}`}
                    onClick={() => remove(enquiry.id)}
                    aria-label={`Delete enquiry from ${enquiry.name}`}
                    className="flex h-9 w-9 items-center justify-center border border-white/10 text-white/40 transition-colors duration-300 hover:border-red-400 hover:text-red-400"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                <h3 className="mt-5 font-display text-lg font-medium tracking-tight md:text-xl" data-testid={`enquiry-name-${i}`}>
                  {enquiry.name}
                </h3>
                <a
                  href={`mailto:${enquiry.email}`}
                  className="mt-1 inline-block font-mono text-[11px] tracking-[0.15em] text-neon transition-colors duration-300 hover:text-cyber"
                  data-testid={`enquiry-email-${i}`}
                >
                  {enquiry.email}
                </a>
                <p className="mt-4 max-w-3xl text-sm leading-relaxed text-secondary" data-testid={`enquiry-message-${i}`}>
                  {enquiry.message}
                </p>
              </motion.article>
            ))}
        </div>
      </div>
    </main>
  );
};

export default Bureau;
